import { useMemo, useRef, useState } from 'react';
import { useCountDown, useMemoizedFn } from 'ahooks';
import { sendVerifyCodeV2, VerifyCodeV2Data } from 'services/api/admin';
import errorToaster from 'utils/errorToaster';
import { useTranslate } from '@tolgee/react';
import { message } from 'antd';

const DEFAULT_COUNTDOWN_INTERVAL = 60000;
const DEFAULT_NOT_RESPONSE_COUNTDOWN_INTERVAL = 41000;

interface Options {
  onNotResponse?: () => void;
}

export const useSMSCaptchaV2 = (optiosn: Options) => {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [targetDate, setTargetDate] = useState<number>();
  const [notResponseTargetDate, setNotResponseTargetDate] = useState<number>();

  const [captchaTimes, setCaptchaTimes] = useState(0);
  const [captchaImage, setCaptchaImage] = useState<string>();

  const ref = useRef<VerifyCodeV2Data | null>(null);

  const [countdown] = useCountDown({
    targetDate,
  });

  useCountDown({
    targetDate: notResponseTargetDate,
    onEnd: () => {
      optiosn?.onNotResponse?.();
    },
  });

  const countdownVerify = useMemoizedFn(() => {
    const now = Date.now();
    setTargetDate(now + DEFAULT_COUNTDOWN_INTERVAL);
    setNotResponseTargetDate(now + DEFAULT_NOT_RESPONSE_COUNTDOWN_INTERVAL);
  });

  const modalVisible = useMemo(() => !!captchaImage, [captchaImage]);
  const closeModal = useMemoizedFn(() => {
    setCaptchaImage(undefined);
    setCaptchaTimes((v) => v + 1);
  });

  const sendSMSCaptchaByPhone = useMemoizedFn(async (data: VerifyCodeV2Data) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await sendVerifyCodeV2(data);
      const code = response.data.code;
      const captchaImage = response.data.data?.captchaImage;
      if (code === 200) {
        if (captchaImage) {
          ref.current = data;
          setCaptchaImage(captchaImage);
        } else {
          countdownVerify();
        }
      } else {
        errorToaster({ response }, t('ViewStartups.startups.form.item.captcha.send.error'));
      }
    } catch (err) {
      errorToaster(err, t('ViewStartups.startups.form.item.captcha.send.error'));
    } finally {
      setLoading(false);
    }
  });

  const confirmCaptchaImage = useMemoizedFn(async (captchaText: string, refresh?: boolean) => {
    if (!ref.current || confirmLoading) {
      return;
    }

    if (!refresh) {
      setConfirmLoading(true);
    }
    try {
      const response = await sendVerifyCodeV2({
        phoneNumber: ref.current.phoneNumber,
        purpose: ref.current.purpose,
        captchaText,
      });
      const code = response.data.code;
      const captchaImage = response.data.data?.captchaImage;
      if (code === 200) {
        if (captchaImage) {
          if (!refresh) {
            message.warning(t('请输入正确的图形验证码'));
          }
          setCaptchaImage(captchaImage);
        } else {
          closeModal();
          countdownVerify();
        }
      } else {
        errorToaster({ response }, t('ViewStartups.startups.form.item.captcha.send.error'));
      }
    } catch (err) {
      errorToaster(err, t('ViewStartups.startups.form.item.captcha.send.error'));
    } finally {
      setConfirmLoading(false);
    }
  });

  const formatCountdown = useMemo(() => {
    return countdown > 0 ? Math.floor(countdown / 1000) : 0;
  }, [countdown]);

  return {
    loading,
    confirmLoading,
    countdown: formatCountdown,
    captchaImage,
    sendSMSCaptchaByPhone,
    confirmCaptchaImage,
    modalVisible,
    closeModal,
    captchaTimes,
  };
};
